import { MouseEvent, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  CacheStorageInfo,
  ClientCacheName,
  deleteClientCache,
  getCacheStorageInfo,
  getClientCacheUrls,
} from '../../data/client_cache_storage.ts';
import { confirm } from '../../util/confirm.tsx';
import { Header } from '../header.tsx';

export const CacheStoragePage = () => (
  <CacheStorageWrapper>
    {' '}
    <CacheStorageList />
  </CacheStorageWrapper>
);

const CacheStorageList = () => {
  const [cacheInfo, setCacheInfo] = useState<CacheStorageInfo[]>(() => []);

  const refreshCacheKeys = useCallback(async () => {
    setCacheInfo(await getCacheStorageInfo());
  }, []);

  const clearAllSettings = useCallback(async () => {
    if (await confirm({ confirmation: <span>Delete all locally cached music?</span> })) {
      await deleteClientCache(ClientCacheName.Hymnals);
      await deleteClientCache(ClientCacheName.CustomMusic);
      await deleteClientCache(ClientCacheName.Vocals);
      await refreshCacheKeys();
    }
  }, [refreshCacheKeys]);

  // Would be nice, but too expensive because this page is rendered along with the settings page
  // Thus, we use the refresh button to see the actual numbers
  // useEffect(() => {
  //   void (async () => {
  //     await refreshCacheKeys();
  //   })();
  // }, [refreshCacheKeys]);
  //
  // useEffect(() => {
  //   const controller = new AbortController();
  //   window.addEventListener('storage', refreshCacheKeys, { signal: controller.signal });
  //   return () => controller.abort();
  // }, [refreshCacheKeys]);

  return (
    <ScrollContainer>
      <ClearAllButton onClick={clearAllSettings}>Clear All Music</ClearAllButton>
      <RefreshButton onClick={refreshCacheKeys}>Refresh</RefreshButton>
      <CacheStorageTableWrapper>
        {cacheInfo.map((info) => (
          <CacheStorageRow key={info.name} info={info} refreshCacheKeys={refreshCacheKeys} />
        ))}
      </CacheStorageTableWrapper>
    </ScrollContainer>
  );
};

function CacheStorageRow({
  info,
  refreshCacheKeys,
}: {
  info: CacheStorageInfo;
  refreshCacheKeys: () => Promise<void>;
}) {
  const { name, count, sizeMB } = info;
  const [isContentVisible, setIsContentVisible] = useState(false);

  const onClick = useCallback(() => {
    setIsContentVisible(true);
  }, []);

  const onCloseCacheContent = useCallback(() => {
    setIsContentVisible(false);
  }, []);

  return (
    <CacheStorageRowWrapper key={name}>
      <CacheStorageName onClick={onClick}>{getShortKeyName(name)}</CacheStorageName>
      <AttributesAndButtonGroup>
        <CacheStorageAttributes>
          {`${count} file${count > 1 ? 's' : ''}, ${sizeMB.toFixed(1)}MB`}
        </CacheStorageAttributes>
        <DeleteButton cacheName={name} refreshCacheKeys={refreshCacheKeys} />
      </AttributesAndButtonGroup>
      {isContentVisible && <CacheContent cacheName={name} onClose={onCloseCacheContent} />}
    </CacheStorageRowWrapper>
  );
}

const DeleteButton = ({
  cacheName,
  refreshCacheKeys,
}: {
  cacheName: ClientCacheName;
  refreshCacheKeys: () => Promise<void>;
}) => {
  const onClick = useCallback(async () => {
    if (cacheName.startsWith('workbox')) {
      if (!(await confirm({ confirmation: <span>WARNING: Delete all application files?</span> }))) {
        return;
      }
    }
    await deleteClientCache(cacheName);
    await refreshCacheKeys();
  }, [cacheName, refreshCacheKeys]);
  return <DeleteButtonStyles onClick={onClick}>Delete</DeleteButtonStyles>;
};

function getShortKeyName(key: string) {
  if (key.startsWith('workbox')) {
    return 'workbox';
  }
  return key;
}

const CacheStorageWrapper = styled.div`
  background-color: var(--color-background);
  height: 100%;
  overflow: auto;
`;

const ButtonBase = styled.button`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 1.2em;
  border-radius: 15px;
  padding: 2px 12px;
  margin: 20px 10px;
  border: none;
  color: var(--color-text-button);
  cursor: pointer;
`;

const ClearAllButton = styled(ButtonBase)`
  background-color: red;
`;

const RefreshButton = styled(ButtonBase)`
  background-color: green;
`;

const ScrollContainer = styled.div`
  position: absolute;
  width: 100vw;
  height: 100%;
  overflow: auto;
`;

const CacheStorageTableWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const CacheStorageRowWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px;
`;

const CacheStorageName = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 1.2em;
  text-align: left;
  cursor: pointer;
`;

const DeleteButtonStyles = styled.button`
  font-family: Jost-SemiBold, sans-serif;
  font-size: 1em;
  color: var(--color-text-button);
  background-color: red;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  padding: 5px 10px;
  height: 100%;
  margin: auto 0;
`;

const CacheStorageAttributes = styled.div``;

const AttributesAndButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

function CacheContent({ cacheName, onClose }: { cacheName: ClientCacheName; onClose: () => void }) {
  const [cacheUrls, setCacheUrls] = useState<string[]>([]);
  const [searchString, setSearchString] = useState('');

  useEffect(() => {
    void (async () => {
      setCacheUrls(await getClientCacheUrls(cacheName));
    })();
  }, [cacheName]);

  const onCloseHandler = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      onClose();
    },
    [onClose],
  );

  const onSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(event.target.value);
  }, []);

  return (
    <CacheContentDialog>
      <CacheContentStacker>
        <Header title={`${cacheName} Cache`} onBack={onCloseHandler}></Header>
        <CacheSearch onChange={onSearchChange} />
        <ScrollHorizontalContainer>
          {cacheUrls
            .filter((url) => !searchString || url.includes(searchString))
            .map((url) => (
              <CacheUrlEntry key={url}>{url}</CacheUrlEntry>
            ))}
        </ScrollHorizontalContainer>
      </CacheContentStacker>
    </CacheContentDialog>
  );
}

const CacheContentDialog = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  height: 100vh;
  width: 100vw;
  background-color: var(--color-background);
`;

const CacheContentStacker = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
`;

const CacheUrlEntry = styled.div`
  text-wrap: nowrap;
`;

const ScrollHorizontalContainer = styled.div`
  flex: 1;
  width: 100vw;
  min-height: 0;
  overflow: auto;
`;

const CacheSearch = styled.input.attrs({ placeholder: 'Search' })`
  width: 100%;
  padding: 10px;
  background-color: var(--color-background);
  color: var(--color-text);
`;
